import type { MutableRefObject } from 'react';
import mapboxgl from 'mapbox-gl';
import {
  getSingleWorldMinZoom,
  lockMapboxToNorthUp,
  MAPBOX_NORTH_UP_OPTIONS,
  MAPBOX_RENDER_WORLD_COPIES,
} from './dashboardPresentation';
import { announceDashboardMap } from './mapboxMapRegistry';
import type { MapboxMap, NetworkMode } from './mapboxTypes';

export const SSE_STALE_RECONNECT_SECONDS = 90;

export const SSE_WATCHDOG_INTERVAL_MS = 15000;

export const weatherLayerOptions = {
  precipitation_new: 'Precipitation',
  temp_new: 'Temperature',
  wind_new: 'Wind Speed',
};

export const lerp = (start: number, end: number, t: number) =>
  start + (end - start) * t;

export const computeStaleSeconds = (
  timestamp?: string | number | Date | null,
  now = Date.now()
): number | null => {
  if (!timestamp) return null;
  const ms = new Date(timestamp).getTime();
  if (!Number.isFinite(ms)) return null;
  const seconds = Math.floor((now - ms) / 1000);
  return Number.isFinite(seconds) && seconds >= 0 ? seconds : null;
};

export const getReconnectDelayMs = (attempt: number) =>
  Math.min(30000, Math.max(2000, 2000 * 2 ** Math.max(0, attempt)));

export const findClosestRoutePointIndex = (
  points:
    Array<{ lat: number | string; long: number | string }> | null | undefined,
  shipmentData?: {
    latest_location?: {
      latest_lat?: number | string;
      latest_long?: number | string;
    };
  } | null
) => {
  if (!Array.isArray(points) || points.length === 0) return 0;
  const lat = Number(shipmentData?.latest_location?.latest_lat);
  const lng = Number(shipmentData?.latest_location?.latest_long);
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) {
    return Math.max(0, points.length - 2);
  }

  let closestIndex = 0;
  let minDistanceSq = Number.POSITIVE_INFINITY;
  points.forEach((p, idx) => {
    const dLat = Number(p.lat) - lat;
    const dLng = Number(p.long) - lng;
    const distanceSq = dLat * dLat + dLng * dLng;
    if (distanceSq < minDistanceSq) {
      minDistanceSq = distanceSq;
      closestIndex = idx;
    }
  });
  return closestIndex;
};

// Overlays (weather tiles, hazard polygons, route lines) must render below
// the shipment markers, matching Google Maps where tile overlays and
// polylines always sit beneath markers.
export const firstShipmentLayerId = (map: MapboxMap) =>
  [
    'shipment-clusters',
    'shipment-cluster-alert-badges',
    'shipment-points',
  ].find((id) => map.getLayer(id));

export const shouldShowMapLoadingOverlay = ({
  isStreamLoading,
  mapVisualReady,
  isShipments,
  mapLoadFailed = false,
}: {
  isStreamLoading: boolean;
  mapVisualReady: boolean;
  isShipments: boolean | unknown[];
  mapLoadFailed?: boolean;
}) =>
  Boolean(
    isShipments && !mapLoadFailed && (isStreamLoading || !mapVisualReady)
  );

/**
 * Mapbox reports recoverable problems -- a missing tile, an absent sprite --
 * on the same `error` event as a rejected access token. Only a rejected token
 * guarantees the style can never arrive, so it is the one error that may
 * replace the map with a failure state; everything else is logged and the map
 * is left to recover on its own.
 */
const MAPBOX_AUTH_ERROR_STATUSES = new Set([401, 403]);

export const isFatalMapboxLoadError = (error?: {
  status?: number;
  statusCode?: number;
}) =>
  MAPBOX_AUTH_ERROR_STATUSES.has(Number(error?.status ?? error?.statusCode));

/**
 * A style request that stalls emits no event at all, so the failure state also
 * needs a deadline.
 *
 * Deliberately generous. Giving up early on a map that was still arriving
 * costs a working user their map, while waiting longer only delays a failure
 * state on a session that was never going to render. Connection hints are
 * unavailable in Safari and Firefox, so the base deadline has to be safe for
 * an undetected slow connection too.
 */
const MAPBOX_LOAD_TIMEOUT_MS = 30000;

const MAPBOX_CONSTRAINED_LOAD_TIMEOUT_MS = 60000;

export const getMapboxLoadTimeoutMs = (networkMode: NetworkMode) =>
  networkMode === 'constrained'
    ? MAPBOX_CONSTRAINED_LOAD_TIMEOUT_MS
    : MAPBOX_LOAD_TIMEOUT_MS;

export const reportMapboxDashboardError = (context: string, error: unknown) => {
  if (process.env.NODE_ENV !== 'production') {
    console.error(`[HomeMapboxMap] ${context}`, error);
  }
};

export const createDashboardMapboxMap = ({
  container,
  style,
  fadeDuration,
  MapConstructor = mapboxgl.Map,
}: {
  container: HTMLElement;
  style: string;
  fadeDuration: number;
  MapConstructor?: typeof mapboxgl.Map;
}): MapboxMap => {
  const map = new MapConstructor({
    container,
    style,
    center: [-118.4036, 33.9422],
    zoom: 2,
    ...MAPBOX_NORTH_UP_OPTIONS,
    minZoom: getSingleWorldMinZoom(container?.clientWidth ?? 0),
    projection: 'mercator',
    renderWorldCopies: MAPBOX_RENDER_WORLD_COPIES,
    attributionControl: false,
    fadeDuration,
  });
  lockMapboxToNorthUp(map);
  announceDashboardMap(map);
  return map;
};

/** Removes every marker held in `markersRef` from the map and empties it. */
export const clearMapMarkers = (
  markersRef: MutableRefObject<mapboxgl.Marker[]>
) => {
  markersRef.current.forEach((marker) => marker.remove());
  markersRef.current = [];
};
